import { useRef, useState, useCallback, useEffect } from "react";

/**
 * Touch helpers — swipe + pull-to-refresh used by the mobile thread list.
 *
 * Kept dependency-free (no Hammer/react-spring) to stay fast on mobile.
 */

/** Detect swipe-left/right gestures on a single element. */
export function useSwipe({ onSwipeLeft, onSwipeRight, threshold = 70 } = {}) {
  const start = useRef(null);
  return {
    onTouchStart: (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      start.current = { x: t.clientX, y: t.clientY };
    },
    onTouchEnd: (e) => {
      const s = start.current;
      start.current = null;
      if (!s) return;
      const t = e.changedTouches?.[0];
      if (!t) return;
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy) * 1.4) return;
      if (dx > 0) onSwipeRight?.();
      else onSwipeLeft?.();
    },
  };
}

/**
 * Pull-to-refresh: returns refs + state for a scroll container.
 *
 *   const { containerRef, indicatorOffset, isRefreshing } = usePullToRefresh(onRefresh);
 *   <div ref={containerRef} className="overflow-y-auto">
 *     <PullIndicator offset={indicatorOffset} />
 *     ...
 *   </div>
 */
export function usePullToRefresh(onRefresh, { threshold = 70, max = 110 } = {}) {
  const containerRef = useRef(null);
  const startY = useRef(null);
  const [offset, setOffset] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const trigger = useCallback(async () => {
    setIsRefreshing(true);
    try { await onRefresh?.(); } finally {
      setOffset(0);
      // small delay so the user sees "released"
      setTimeout(() => setIsRefreshing(false), 200);
    }
  }, [onRefresh]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ts = (e) => {
      if (el.scrollTop > 0) { startY.current = null; return; }
      const t = e.touches?.[0]; if (!t) return;
      startY.current = t.clientY;
    };
    const tm = (e) => {
      if (startY.current == null || isRefreshing) return;
      const t = e.touches?.[0]; if (!t) return;
      const dy = t.clientY - startY.current;
      if (dy <= 0) { setOffset(0); return; }
      // dampen
      setOffset(Math.min(max, dy * 0.55));
    };
    const te = () => {
      if (startY.current == null) return;
      startY.current = null;
      if (offset >= threshold) trigger();
      else setOffset(0);
    };
    el.addEventListener("touchstart", ts, { passive: true });
    el.addEventListener("touchmove", tm, { passive: true });
    el.addEventListener("touchend", te);
    return () => {
      el.removeEventListener("touchstart", ts);
      el.removeEventListener("touchmove", tm);
      el.removeEventListener("touchend", te);
    };
  }, [offset, threshold, max, trigger, isRefreshing]);

  return { containerRef, indicatorOffset: offset, isRefreshing };
}
